import { Button, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { getNonNullUserName } from '@civilgrid/ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { joinProject, leaveProject } from '~/api/projects2';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import Notify from '~/components2/Notify';
import { useGetProject } from '~/hooks/useGetProject';
import { InviteTeamProjectDialog } from '~/pages/common/Invite';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import RemoveMember from './RemoveMember';

export function TabDetails() {
  const { currentOrganizationDetail, me } = useSnapshot(rootStore);
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const projectRequest = useGetProject({ projectId }, { refetchOnWindowFocus: false });
  const project = projectRequest.data;
  const queryClient = useQueryClient();

  const handleJoinProject = async () => {
    try {
      await joinProject(projectId);
      queryClient.invalidateQueries({ queryKey: ['project', { projectId }] });
      Notify.info({
        title: 'Joined project',
        message: `You have successfully joined the project ${project?.name}`,
      });
    } catch (e) {
      console.error(e);

      Notify.error({
        title: 'Error joining project',
        message: 'Error joining project. Please contact support if this error persists',
      });
    }
  };

  const handleLeaveProject = async () => {
    try {
      await leaveProject(projectId);
      queryClient.invalidateQueries({ queryKey: ['project', { projectId }] });
      Notify.info({
        title: 'Left project',
        message: `You have successfully left the project ${project?.name}`,
      });
    } catch (e) {
      console.error(e);

      Notify.error({
        title: 'Error leaving project',
        message: 'Error leaving project. Please contact support if this error persists',
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (currentOrganizationDetail.isLoading || !project) {
      return null;
    }

    const tableStyle = 'relative table-fixed w-full';
    const headingRowStyle = 'sticky top-0 bg-white text-left text-md font-medium';
    const headingCellStyle = 'w-3/4 truncate pl-1 font-semibold h-8';
    const rowStyle = 'hover:bg-secondary/5 group';
    // Make first column narrower with max-width
    const firstColumnStyle = 'w-3/4 truncate pl-1 py-1 align-middle'; // Adjust w-48 as needed
    const cellStyle = 'w-1/12 truncate pl-1 py-1 align-middle';
    const hiddenCellStyle = `w-1/4 align-middle opacity-0 group-hover:opacity-100 focus-within:opacity-100 text-center`;

    const isCurrentUser = (member: any) => member.user.id === me?.id;

    // Create a sorted array of active members
    const sortedMembers = project.members
      .filter((m) => m.user.status === 'active' || m.user.status === 'invited')
      .sort((a, b) => {
        // invited users at the end
        if (a.user.status === 'invited' && b.user.status !== 'invited') return 1;
        if (b.user.status === 'invited' && a.user.status !== 'invited') return -1;

        // current user at top (if not invited)
        if (a.user.status !== 'invited') {
          if (isCurrentUser(a)) return -1;
          if (isCurrentUser(b)) return 1;
        }

        // sort alphabetically within each group
        return a.user.name.localeCompare(b.user.name);
      });

    return (
      <table className={tableStyle}>
        <thead>
          <tr className={headingRowStyle}>
            <th className={headingCellStyle}>Members</th>
          </tr>
        </thead>
        <tbody>
          {sortedMembers.map((m) => {
            const name = getNonNullUserName(m.user.name);
            const email = m.user.email ?? '';
            return (
              <tr key={m.id} className={rowStyle}>
                <td className={firstColumnStyle}>
                  <div className="flex flex-row items-start space-x-2">
                    <div>
                      <div className="truncate text-sm">{name || email}</div>
                      <div className="truncate text-xs text-secondary">{email}</div>
                    </div>
                    <span className="lowercase text-primary-light text-sm">
                      {isCurrentUser(m) ? ' (you)' : m.user.status === 'invited' ? ' (invited)' : ''}
                    </span>
                  </div>
                </td>
                <td className={cellStyle}></td>
                <td className={hiddenCellStyle}>
                  {isCurrentUser(m) ? (
                    <div className="flex justify-end">
                      <Button
                        variant="secondary"
                        onClick={handleLeaveProject}
                        disabled={project.projectManager.id?.toString() === m.user.id.toString()}
                        tooltip="The project manager cannot leave the project. Please reassign the PM before leaving."
                        data-testid="project-team-leave-button"
                      >
                        Leave
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-end">
                      <RemoveMember member={m} />
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }, [currentOrganizationDetail.isLoading, project, me?.id]);

  if (!project) {
    return <SkeletonSpinnerCentered />;
  }

  const isUserInProject = project.members.some((m) => m.user.id === me?.id);

  return (
    <div className="flex w-full flex-auto flex-col space-y-2 pb-24">
      <div className="flex flex-row justify-end">
        {isUserInProject ? (
          <Can action={Action.AddProjectMember}>
            <InviteTeamProjectDialog
              trigger={<Button data-testid="project-team-invite-button">Invite Team</Button>}
              projectId={projectId}
              projectMemberUserIds={project.members.map((m) => String(m.user.id))}
              onSave={() => queryClient.invalidateQueries({ queryKey: ['project', { projectId }] })}
            />
          </Can>
        ) : (
          <Can action={Action.AddProjectMember}>
            <Button variant="secondary" onClick={handleJoinProject} data-testid="project-team-join-button">
              Join Project
            </Button>
          </Can>
        )}
      </div>

      <div className="flex flex-col space-y-1">{content}</div>
    </div>
  );
}

export function MapChildren() {
  return null;
}
